import { LitElement, html, css } from 'lit-element';
import { debounce, generateProfileImageByInitials } from '../../../lib/helpers';
import { Image } from '../Image/Image';
import { drawerDialogService } from '../Dialog-Drawer/dialog-drawer';

export class BlockedUsers extends LitElement {
  static get properties() {
    return {
      blockedUsers: { type: Array },
      isLoading: { type: Boolean },
      paginationLoading: { type: Boolean },
      hasMore: { type: Boolean },
      handleUnblock: { type: Function },
      goBack: { type: Function },
      findBlokedUser: { type: Function },
      skip: { type: Number },
    };
  }

  constructor() {
    super();
    this.blockedUsers = [];
    this.isLoading = false;
    this.handleUnblock = () => {};
    this.goBack = () => {};
    this.searchTerm = '';
    this.debouncedSearchHandler = debounce(query => {
      this.findBlokedUser(query);
    }, 500);
  }
  handleScroll(event) {
    const element = event.target;
    const threshold = 10;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold && this.hasMore && !this.paginationLoading) {
      this.findBlokedUser(null, this.skip + 20);
    }
  }
  handleSearch(e) {
    this.searchTerm = e.target.value;
    this.debouncedSearchHandler(this.searchTerm);
  }
  unBlockUser(user){
     drawerDialogService.openDialog('confirm-dialog', { 
             title: `Unblock`, 
             description: `Are you sure you want to unblock ${user.userName} ?`,
             handleConfirm:() => this.handleUnblock(user.userId)
           });
  }
  render() {
    return html`
      <div class="blocked-users-container">
        <div class="header">
          <button class="back-button" @click=${this.goBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <h2>Blocked Users</h2>
        </div>
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg
              class="search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input type="text" class="search-input" placeholder="Search users..." .value=${this.searchTerm} @input=${this.handleSearch} />
            ${this.searchTerm
              ? html`
                  <button
                    class="clear-search"
                    @click=${() => {
                      this.searchTerm = '';
                      this.shadowRoot.querySelector('.search-input').value = '';
                      this.findBlokedUser();
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                `
              : ''}
          </div>
        </div>

        ${this.isLoading
          ? html`
              <div class="loading-spinner">
                <spinner-loader
                  height="30px"
                  width="30px"
                  spinTime="1s"
                  spinnerPrimaryColor="var(--bg-primary-color)"
                  spinnerSecondaryColor="var(--text-primary-color)"
                  spinnerBorderWidth="2px"
                >
                </spinner-loader>
              </div>
            `
          : this.blockedUsers?.length === 0
          ? html`<div class="empty-state">${this.searchTerm ? `No users found matching "${this.searchTerm}".` : "You haven't blocked any users yet."}</div>`
          : html`
              <div class="users-list" @scroll=${this.handleScroll}>
                ${this.blockedUsers?.map(
                  user => html`
                    <div class="user-item">
                      <div class="user-info">
                        <div class="user-avatar">
                          ${user.userProfileImageUrl
                            ? html`${Image({
                                src: user.userProfileImageUrl,
                                alt: `${user.userName}'s profile picture`,
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                placeholderImage: generateProfileImageByInitials(user.userName, 40, 40),
                              })}`
                            : html`<div class="avatar-placeholder">${generateProfileImageByInitials(user.userName)}</div>`}
                        </div>
                        <div class="user-name">${user.userName}</div>
                      </div>
                      <button class="unblock-button" @click=${() => this.unBlockUser(user)}>Unblock</button>
                    </div>
                  `,
                )}
                ${this.paginationLoading
                  ? html`
                      <div>
                        <spinner-loader
                          height="30px"
                          width="30px"
                          spinTime="1s"
                          spinnerPrimaryColor="var(--bg-primary-color)"
                          spinnerSecondaryColor="var(--text-primary-color)"
                          spinnerBorderWidth="2px"
                        >
                        </spinner-loader>
                      </div>
                    `
                  : null}
              </div>
            `}
      </div>
    `;
  }

  static get styles() {
    return css`
      :host {
        height: 96% !important;
      }
      .blocked-users-container {
        padding: 5px;
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .header {
        display: flex;
        align-items: center;
        padding: 10px 0;
        margin-bottom: 20px;
        border-bottom: 1px solid var(--bg-grey-color);
      }

      .back-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 5px;
        display: flex;
        align-items: center;
        color: var(--text-outgoing-message);
      }

      h2 {
        margin: 0 0 0 10px;
        font-size: 22px;
        font-weight: 500;
        color: var(--text-outgoing-message);
      }

      .loading-spinner {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: var(--text-secondary-color);
        font-size: 16px;
      }

      .users-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        height: calc(100vh - 100px);
        overflow-y: auto;
      }

      .user-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px;
        border-bottom: 1px solid var(--bg-grey-color);
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        overflow: hidden;
      }
      .search-container {
        margin-bottom: 20px;
      }

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
      }

      .search-icon {
        position: absolute;
        left: 10px;
        color: var(--text-secondary-color);
      }

      .search-input {
        width: 100%;
        padding: 10px 10px 10px 36px;
        border-radius: 8px;
        border: 1px solid var(--bg-grey-color);
        background-color: var(--bg-grey-color);
        color: var(--text-outgoing-message);
        font-size: 14px;
      }

      .search-input:focus {
        outline: none;
        border-color: var(--bg-incoming-message);
      }

      .clear-search {
        position: absolute;
        right: 10px;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--text-secondary-color);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
      }

      .avatar-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--bg-incoming-message);
        color: var(--text-incoming-message);
        font-weight: 500;
        font-size: 16px;
      }
      .user-name {
        font-size: 16px;
        color: var(--text-outgoing-message);
      }
      .unblock-button {
        background-color: var(--base-color);
        color: var(--text-outgoing-message);
        border: none;
        border-radius: 4px;
        padding: 6px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background-color 0.2s;
      }
      .unblock-button:hover {
        background-color: var(--bg-incoming-message);
        color: var(--text-incoming-message);
      }
    `;
  }
}

customElements.define('blocked-users-list', BlockedUsers);
